import React, { useCallback, useEffect, useState } from "react";
import * as Sentry from "@sentry/react-native";

import AddImageCard from "./Components/AddImageCard";

import MediaService from "@/services/user/media";
import {
  UserImageType,
  type UserLikableImageMedia,
} from "@/schemas/user/image";
import ImageGrid from "@/Components/Media/User/Layout/UserImageGrid";
import { useHeaderContext } from "@/context/header/useHeaderContext";
import { type AppUserSession } from "@/schemas/auth/session";
import { MediaManagerTabs, Screen } from "@/schemas/screen/tabs";
import universalUploadImage from "@/utils/image/universalUploadImage";
import UserImageActionableCard from "@/Components/Media/User/Images/UserImageActionableCard";
import { transformUserImagesToUserLikableImages } from "@/utils/image/images";

type ImageUploadScreenProps = {
  session: AppUserSession;
};

function getImageType(selectedTab: MediaManagerTabs): UserImageType {
  switch (selectedTab) {
    case MediaManagerTabs.PUBLIC:
      return UserImageType.PUBLIC_IMAGE;
    case MediaManagerTabs.PRIVATE:
      return UserImageType.PRIVATE_IMAGE;
    default:
      throw Error(`Not supported tab ${selectedTab}`);
  }
}

function MediaManagerScreen({
  session,
}: ImageUploadScreenProps): React.JSX.Element {
  const { currentScreen } = useHeaderContext();
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [currentTab, setCurrentTab] = useState<MediaManagerTabs>(
    MediaManagerTabs.PUBLIC,
  );
  const [images, setImages] = useState<UserLikableImageMedia[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);

  useEffect(() => {
    if (currentScreen.screenId === Screen.MEDIA_MANAGER) {
      setCurrentTab(currentScreen.activeTabId);
      fetchImages(currentScreen.activeTabId);
    }
  }, [currentScreen]);

  const fetchImages = async (currentTab: MediaManagerTabs): Promise<void> => {
    setIsLoading(true);
    const imageType = getImageType(currentTab);
    try {
      const ms = new MediaService(session);
      // Get the first 9 images because there is a card to add a new image.
      const response = await ms.getImages(
        session.identity.username,
        imageType,
        { limit: 9, next: null },
      );
      setImages(response.items);
      setNextCursor(response.next);
    } finally {
      setIsLoading(false);
    }
  };

  const loadMoreImages = async (): Promise<void> => {
    if (!nextCursor) return;

    setIsLoadingMore(true);
    const imageType = getImageType(currentTab);

    try {
      const ms = new MediaService(session);
      // This should be an even number since we already included the card.
      const response = await ms.getImages(
        session.identity.username,
        imageType,
        { limit: 10, next: nextCursor },
      );

      setImages([...images, ...response.items]);
      setNextCursor(response.next);
    } catch (error) {
      Sentry.captureException(error);
    } finally {
      setIsLoadingMore(false);
    }
  };

  const onRefresh = useCallback(async () => {
    await fetchImages(currentTab);
  }, [currentTab]);

  const handleImageUpload = async (): Promise<void> => {
    await universalUploadImage({
      session,
      imageType: getImageType(currentTab),
      onUploadStart: () => setIsLoading(true),
      onUploadSuccess: (userImages) => {
        if (userImages && userImages.length > 0) {
          const userLikableImages =
            transformUserImagesToUserLikableImages(userImages);
          setImages([...userLikableImages, ...images]);
        }
      },
      onUploadEnd: () => setIsLoading(false),
    });
  };

  const renderImageOrActionCard = ({
    item,
  }: {
    item: UserLikableImageMedia | null;
  }): React.JSX.Element => {
    if (item === null) {
      return (
        <AddImageCard
          isPrivateTab={currentTab === MediaManagerTabs.PRIVATE}
          handleImageUpload={handleImageUpload}
          numColumns={2}
        />
      );
    } else {
      return (
        <UserImageActionableCard
          session={session}
          userLikableImage={item}
          numColumns={2}
          isReadOnly={false}
          isPixelated={false}
        />
      );
    }
  };

  return (
    <ImageGrid
      images={[null, ...images]}
      isLoading={isLoading}
      isLoadingMore={isLoadingMore}
      onLoadMore={loadMoreImages}
      onRefresh={onRefresh}
      renderImage={renderImageOrActionCard}
    />
  );
}

export default MediaManagerScreen;
